import moment from 'moment'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap'
import FilterDropdown from 'src/marketConnect/helpers/components/FilterDropdown'
import DataTable from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBan,
  faChevronDown,
  faChevronUp,
  faDownload,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'src/redux/store'
import {
  cancelOverageInvoiceAsync,
  downloadInvoiceAsync,
  getInvoiceDeatilsListAsync,
} from 'src/redux/actions/invoiceDetailsGenerateActions'
import { useSelector } from 'react-redux'
import {
  formatBillingDateOnly,
  formatBillingDateTime,
  formatBillingMonthYear,
} from './billingDateFormat'
import { formatTimeZone } from 'src/utils/TimeZoneDateFormat'
import useAppToast, { getApiErrorMsg } from 'src/hooks/useAppToast'
import { useDebounce } from 'src/hooks/useDebounce'
import TableLoadingShell from 'src/utils/TableLoadingShell'
import CommonConfirmationModal from 'src/utils/modals/ConfirmationModal'
import { useUserHavingWriteAccess } from 'src/utils/useUserHavingWriteAccess'
import './InvoiceDetailsOveragePanel.css'

// Common reusable cell
const CommonColumn: FC<{ rowData: string | number; className?: string }> = ({
  rowData,
  className,
}) => <p className={className ? `mb-0 ${className}` : 'mb-0'}>{rowData}</p>

const getInvoiceBillingDateSource = (row: any): string | null | undefined =>
  row?.bill_paid_at === null ? row?.created_at : row?.bill_paid_at

const InvoiceColumnCell: FC<{ row: any }> = ({ row }) => {
  const invoiceId = row?.id
  const monthYear = formatBillingMonthYear(getInvoiceBillingDateSource(row))
  const invoiceNumberLabel =
    invoiceId !== null && invoiceId !== undefined && invoiceId !== '' ? `#${invoiceId}` : '-'

  return (
    <div className='d-flex flex-column align-items-center'>
      <div className='fw-semibold text-gray-800 mb-0'>{invoiceNumberLabel}</div>
      {monthYear ? <div className='text-muted fs-7 mb-0 mt-1'>{monthYear}</div> : null}
    </div>
  )
}

type BillingType = 'Monthly' | 'Yearly' | 'Overage'

const MONTHLY_FREQUENCIES = new Set(['MONTHLY', 'EVERY_30_DAYS'])
const YEARLY_FREQUENCIES = new Set(['YEARLY', 'ANNUAL', 'EVERY_365_DAYS'])

const BILLING_TYPE_PILL_STYLES: Record<BillingType, { background: string; color: string }> = {
  Monthly: { background: '#DBEAFE', color: '#1D4ED8' },
  Yearly: { background: '#EDE9FE', color: '#6D28D9' },
  Overage: { background: '#FEF3C7', color: '#B45309' },
}

const isOverageTransaction = (row: any): boolean =>
  String(row?.transaction_type ?? '').toUpperCase() === 'OVERAGE'

const parseFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const formatOrderCount = (value: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)

const formatUsdAmount = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value)

const getTransactionDisplayAmount = (row: any): number | null => {
  const withTax = row?.bill_amount_with_tax
  if (withTax !== '' && withTax !== null && withTax !== undefined) {
    return parseFiniteNumber(withTax)
  }
  return parseFiniteNumber(row?.amount_paid)
}

type UsageSummaryMetric = { label: string; value: string }

const UsageSummaryMetricCell: FC<{ metric: UsageSummaryMetric }> = ({ metric }) => (
  <div className='invoice-overage-metric-cell'>
    <div className='text-muted fs-7 fw-semibold mb-1'>{metric.label}</div>
    <div className='fs-6 fw-semibold text-gray-800 text-break'>{metric.value}</div>
  </div>
)

const buildOverageUsageSummaryMetrics = (row: any): UsageSummaryMetric[] => {
  const info = row?.user_transaction_additional_info
  if (!info) return []

  const metrics: UsageSummaryMetric[] = []
  const totalOrders = parseFiniteNumber(info.total_orders)
  const extraOrders = parseFiniteNumber(info.extra_orders)
  const orderLimit = parseFiniteNumber(row?.billing_plan?.order_limit)

  if (orderLimit !== null) {
    metrics.push({
      label: 'Allowed Orders',
      value: formatOrderCount(orderLimit),
    })
  }

  if (totalOrders !== null) {
    metrics.push({
      label: 'Received Orders',
      value: formatOrderCount(totalOrders),
    })
  }

  if (extraOrders !== null) {
    const formatted = formatOrderCount(extraOrders)
    metrics.push({
      label: 'Extra Orders',
      value: extraOrders > 0 ? `+${formatted}` : formatted,
    })
  }

  const perOrderCost = parseFiniteNumber(info.per_order_cost)
  if (perOrderCost !== null) {
    metrics.push({
      label: 'Overage Rate',
      value: `${formatUsdAmount(perOrderCost)}/order`,
    })
  }

  const durationDays = parseFiniteNumber(info.duration)
  if (durationDays !== null) {
    metrics.push({
      label: 'Billing Duration',
      value: `${formatOrderCount(durationDays)} days`,
    })
  }

  const invoiceAmount = getTransactionDisplayAmount(row)
  if (invoiceAmount !== null) {
    metrics.push({
      label: 'Overage Invoice Amount',
      value: formatUsdAmount(invoiceAmount),
    })
  }

  return metrics
}

const hasExpandableOverageDetails = (row: any): boolean =>
  isOverageTransaction(row) && buildOverageUsageSummaryMetrics(row).length > 0

const USAGE_SUMMARY_ROWS: ReadonlyArray<readonly string[]> = [
  ['Allowed Orders', 'Received Orders', 'Extra Orders'],
  ['Overage Rate', 'Billing Duration', 'Overage Invoice Amount'],
]

const OverageUsageSummaryPanel: FC<{ row: any }> = ({ row }) => {
  const metrics = buildOverageUsageSummaryMetrics(row)

  if (!metrics.length) {
    return (
      <div className='invoice-overage-expanded-panel-wrap'>
        <div className='invoice-overage-panel'>
          <span className='text-muted fs-7'>
            No overage usage details available for this transaction.
          </span>
        </div>
      </div>
    )
  }

  const metricsByLabel = new Map(metrics.map((metric) => [metric.label, metric]))
  const visibleMetrics = USAGE_SUMMARY_ROWS.flatMap((rowLabels) =>
    rowLabels
      .map((label) => metricsByLabel.get(label))
      .filter((metric): metric is UsageSummaryMetric => Boolean(metric))
  )

  return (
    <div className='invoice-overage-expanded-panel-wrap'>
      <div className='invoice-overage-panel'>
        <div className='invoice-overage-summary-title fs-6 fw-semibold text-gray-800'>
          Usage Summary
        </div>
        <div className='invoice-overage-metrics-grid'>
          {visibleMetrics.map((metric) => (
            <UsageSummaryMetricCell key={metric.label} metric={metric} />
          ))}
        </div>
      </div>
    </div>
  )
}

const getBillingType = (row: any): BillingType | null => {
  if (isOverageTransaction(row)) {
    return 'Overage'
  }

  const frequency = String(row?.billing_frequency ?? '').toUpperCase()
  if (MONTHLY_FREQUENCIES.has(frequency)) return 'Monthly'
  if (YEARLY_FREQUENCIES.has(frequency)) return 'Yearly'
  return null
}

const BILLING_TYPE_FILTER_KEY = 'billing_type_display'
const BILLING_CHARGE_YEAR_KEY = 'billing_charge_year'
const BILLING_CHARGE_MONTH_KEY = 'billing_charge_month'
const BILLING_TYPE_DISPLAY_ORDER: BillingType[] = ['Monthly', 'Yearly', 'Overage']
const INVOICE_BILLING_TYPE_FILTER_ID = 'invoice-billing-type-filter'
const INVOICE_CHARGE_DATE_FILTER_ID = 'invoice-charge-date-filter'
const CHARGE_MONTH_NAME_OPTIONS = moment.months()
/** Rolling year list for Charge Date filter dropdown. */
const CHARGE_YEAR_FILTER_OPTIONS = Array.from({ length: 15 }, (_, index) =>
  String(moment().year() - index)
)

const stopTableHeaderPointer = (event: { stopPropagation: () => void }) => {
  event.stopPropagation()
}

const formatPeriodDate = (value?: string | null): string => formatBillingDateOnly(value)

const hasChargeDateFilterSelection = (filters: Record<string, string>): boolean =>
  Boolean((filters[BILLING_CHARGE_YEAR_KEY] || '').trim()) ||
  Boolean((filters[BILLING_CHARGE_MONTH_KEY] || '').trim())

const getBillingPeriodTooltip = (row: any): string => {
  const startLabel = formatPeriodDate(row?.bill_paid_at || row?.created_at)
  const endLabel = formatPeriodDate(row?.subscription_end_date)
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`
  return startLabel || endLabel
}

type BillingTypeCellProps = {
  row: any
  canExpand?: boolean
  isExpanded?: boolean
  onToggleExpand?: () => void
}

const BillingTypeCell: FC<BillingTypeCellProps> = ({
  row,
  canExpand = false,
  isExpanded = false,
  onToggleExpand,
}) => {
  const billingType = getBillingType(row)
  if (!billingType) return <CommonColumn rowData='-' />

  const period = getBillingPeriodTooltip(row)
  const pillStyle = BILLING_TYPE_PILL_STYLES[billingType]
  const pillBody = (
    <>
      {billingType}
      {canExpand ? (
        <FontAwesomeIcon
          icon={isExpanded ? faChevronUp : faChevronDown}
          className='invoice-overage-badge-chevron'
          aria-hidden
        />
      ) : null}
    </>
  )

  const pillStyleProps = {
    backgroundColor: pillStyle.background,
    color: pillStyle.color,
    padding: '6px 12px',
    fontSize: '12px',
    cursor: canExpand || period ? 'pointer' : 'default',
  }

  const pill = canExpand ? (
    <button
      type='button'
      className='badge rounded-pill fw-semibold border-0 invoice-overage-badge-toggle'
      style={pillStyleProps}
      aria-expanded={isExpanded}
      aria-label={isExpanded ? 'Collapse overage details' : 'Expand overage details'}
      onClick={(event) => {
        event.stopPropagation()
        onToggleExpand?.()
      }}
    >
      {pillBody}
    </button>
  ) : (
    <span className='badge rounded-pill fw-semibold' style={pillStyleProps}>
      {pillBody}
    </span>
  )

  if (!period) {
    return pill
  }

  return (
    <OverlayTrigger placement='top' overlay={<Tooltip>{period}</Tooltip>}>
      <span className='d-inline-flex'>{pill}</span>
    </OverlayTrigger>
  )
}

type PlanAndBillingTypeCellProps = {
  row: any
  canExpand?: boolean
  isExpanded?: boolean
  onToggleExpand?: () => void
}

const PlanAndBillingTypeCell: FC<PlanAndBillingTypeCellProps> = ({
  row,
  canExpand = false,
  isExpanded = false,
  onToggleExpand,
}) => {
  const planName = row?.billing_plan?.display_name

  return (
    <div className='d-flex flex-column align-items-center gap-2'>
      <div className='fw-semibold text-gray-800 mb-0 text-center text-break w-100'>
        {planName || '-'}
      </div>
      <BillingTypeCell
        row={row}
        canExpand={canExpand}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
      />
    </div>
  )
}

const InvoiceDetailsGeneration: FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { successToast, errorToast } = useAppToast();

  const [paginationData, setPaginationData] = useState({
    page_num: 1,
    page_limit: 10,
    sort_by: "created_at",
    sort_order: "desc",
  });
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebounce(searchInput, 2000);
  const [listSearchStr, setListSearchStr] = useState('');
  const { fetchedUserAccountData, fetchedUserStaffData } = useSelector((state: any) => state.userAccountData);
  const { invoiceDetails, pagination, loading } = useSelector(
    (state: any) => state.invoiceDetailGenerationSlice
  );
  const { isUserHavingWriteAccess } = useUserHavingWriteAccess();
  const canModifyCustomers = isUserHavingWriteAccess();
  // Row showing loader download
  const [downloadingRowId, setDownloadingRowId] = useState<number | null>(null);
  const [isDownloadInProgress, setIsDownloadInProgress] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelTargetRow, setCancelTargetRow] = useState<any | null>(null);
  const [cancelingRowId, setCancelingRowId] = useState<number | null>(null);
  const [expandedRowIds, setExpandedRowIds] = useState<Record<number, boolean>>({});

  const toggleRowExpansion = (rowId: number) => {
    setExpandedRowIds((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  useEffect(() => {
    if (searchInput.trim() === '') {
      setListSearchStr((prev) => {
        if (prev === '') return prev;
        setPaginationData((pagination) =>
          pagination.page_num === 1 ? pagination : { ...pagination, page_num: 1 }
        );
        return '';
      });
    }
  }, [searchInput]);

  useEffect(() => {
    const trimmed = debouncedSearchInput.trim();
    setListSearchStr((prev) => {
      if (prev === trimmed) return prev;
      setPaginationData((pagination) =>
        pagination.page_num === 1 ? pagination : { ...pagination, page_num: 1 }
      );
      return trimmed;
    });
  }, [debouncedSearchInput]);

  useEffect(() => {
    setPaginationData((prev) => (prev.page_num === 1 ? prev : { ...prev, page_num: 1 }));
  }, [filters[BILLING_CHARGE_YEAR_KEY], filters[BILLING_CHARGE_MONTH_KEY]]);

  const fetchInvoiceDetails = useCallback(() => {
    dispatch(
      getInvoiceDeatilsListAsync({
        paramData: {
          page: paginationData.page_num,
          limit: paginationData.page_limit,
          sort_by: paginationData.sort_by,
          sort_order: paginationData.sort_order,
          searchStr: listSearchStr,
          chargeYear: filters[BILLING_CHARGE_YEAR_KEY] || '',
          chargeMonthName: filters[BILLING_CHARGE_MONTH_KEY] || '',
        },
      })
    );
  }, [dispatch, filters, listSearchStr, paginationData]);

  const billingTypeFilter = (filters[BILLING_TYPE_FILTER_KEY] || '').trim();
  const hasActiveInvoiceFilters = useMemo(
    () =>
      Boolean(searchInput.trim()) ||
      Boolean(billingTypeFilter) ||
      hasChargeDateFilterSelection(filters),
    [searchInput, billingTypeFilter, filters]
  );

  const handleResetFilters = () => {
    if (!hasActiveInvoiceFilters) {
      return;
    }
    setOpenFilterKey(null);
    setFilters({});
    setSearchInput('');
    setPaginationData((prev) =>
      prev.page_num === 1 ? prev : { ...prev, page_num: 1 }
    );
  };

  const tableRows = useMemo(() => {
    if (!billingTypeFilter) return invoiceDetails;
    return invoiceDetails.filter((row) => getBillingType(row) === billingTypeFilter);
  }, [billingTypeFilter, invoiceDetails]);

  useEffect(() => {
    fetchInvoiceDetails();
  }, [fetchInvoiceDetails]);

  const handleDownload = async (row: any) => {
    try {
      setDownloadingRowId(row.id);
      setIsDownloadInProgress(true);

      const result: any = await dispatch(
        downloadInvoiceAsync({ transaction_id: row.id })
      ).unwrap();

      if (result.success) {
        successToast('Invoice downloaded successfully!');
      }
    } catch (error) {
      errorToast('Failed to download invoice. Please try again.');
    } finally {
      setDownloadingRowId(null);
      setIsDownloadInProgress(false);
    }
  };

  const handleCancelInvoice = async () => {
    if (!cancelTargetRow) return;

    try {
      setCancelingRowId(cancelTargetRow.id);
      await dispatch(cancelOverageInvoiceAsync(cancelTargetRow.id)).unwrap();
      successToast('Overage invoice cancelled successfully.');
      setShowCancelConfirm(false);
      setCancelTargetRow(null);
      fetchInvoiceDetails();
    } catch (error) {
      errorToast(getApiErrorMsg(error, 'Failed to cancel invoice. Please try again.'));
    } finally {
      setCancelingRowId(null);
    }
  };

  const handleSort = (column: any, sortDirection: 'asc' | 'desc') => {
    setPaginationData((prev) => ({
      ...prev,
      sort_by: column.sortField,
      sort_order: sortDirection,
      page_num: 1,
    }));
  };

  const columns = useMemo(
    () => [
    {
      name: 'Invoice',
      cell: (row: any) => <InvoiceColumnCell row={row} />,
      sortable: false,
      width: '125px',
      minWidth: '125px',
      center: true,
    },
    {
      name: (
        <div className='invoice-details-col-header'>
          <span>Plan & billing type</span>
          <span
            className='invoice-details-col-header__filter-hit'
            onClick={stopTableHeaderPointer}
            onMouseDown={stopTableHeaderPointer}
          >
            <FilterDropdown
              id={INVOICE_BILLING_TYPE_FILTER_ID}
              filters={filters}
              setFilters={setFilters}
              openFilterKey={openFilterKey}
              setOpenFilterKey={setOpenFilterKey}
              hasActiveFilter={Boolean((filters[BILLING_TYPE_FILTER_KEY] || '').trim())}
              options={[
                {
                  label: 'Billing type',
                  key: BILLING_TYPE_FILTER_KEY,
                  values: [...BILLING_TYPE_DISPLAY_ORDER],
                },
              ]}
            />
          </span>
        </div>
      ),
      cell: (row: any) => (
        <PlanAndBillingTypeCell
          row={row}
          canExpand={hasExpandableOverageDetails(row)}
          isExpanded={!!expandedRowIds[row.id]}
          onToggleExpand={() => toggleRowExpansion(row.id)}
        />
      ),
      sortable: false,
      width: '270px',
      minWidth: '270px',
      center: true,
    },
    {
      name: 'Amount',
      cell: (row: any) => (
        <CommonColumn
          className='invoice-details-nowrap'
          rowData={
            row?.bill_amount_with_tax === '' || row?.bill_amount_with_tax === null
              ? row.amount_paid
              : row.bill_amount_with_tax
          }
        />
      ),
      sortable: false,
      center: true,
      width: '120px',
      minWidth: '120px',
    },
    {
      name: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          Payment Method
          <OverlayTrigger
            placement='top'
            overlay={<Tooltip>Method used to process payment</Tooltip>}
          >
            <span style={{ cursor: 'pointer', color: "darkgray" }}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          </OverlayTrigger>
        </div>
      ),
      cell: (row: any) => (
        <CommonColumn className='invoice-details-nowrap' rowData={row.payment_method} />
      ),
      center: true,
      width: '170px',
      minWidth: '170px',
    },
    {
      name: (
        <div className='invoice-details-col-header'>
          <span>Charge Date</span>
          <OverlayTrigger
            placement='top'
            overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
          >
            <span
              style={{ cursor: 'pointer', color: 'darkgray' }}
              onClick={stopTableHeaderPointer}
              onMouseDown={stopTableHeaderPointer}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          </OverlayTrigger>
          <span
            className='invoice-details-col-header__filter-hit'
            onClick={stopTableHeaderPointer}
            onMouseDown={stopTableHeaderPointer}
          >
            <FilterDropdown
              id={INVOICE_CHARGE_DATE_FILTER_ID}
              filters={filters}
              setFilters={setFilters}
              openFilterKey={openFilterKey}
              setOpenFilterKey={setOpenFilterKey}
              hasActiveFilter={hasChargeDateFilterSelection(filters)}
              options={[
                {
                  label: 'Year',
                  key: BILLING_CHARGE_YEAR_KEY,
                  values: CHARGE_YEAR_FILTER_OPTIONS,
                },
                {
                  label: 'Month',
                  key: BILLING_CHARGE_MONTH_KEY,
                  values: CHARGE_MONTH_NAME_OPTIONS,
                },
              ]}
            />
          </span>
        </div>
      ),
      cell: (row: any) => (
        <CommonColumn
          className='invoice-details-nowrap'
          rowData={
            row?.bill_paid_at === null
              ? formatBillingDateTime(row.created_at)
              : formatBillingDateTime(row?.bill_paid_at)
          }
        />
      ),
      sortable: false,
      center: true,
      width: '170px',
      minWidth: '170px',
    },
    {
      name: 'Payment Status',
      cell: (row: any) => (
        <span
          className={`badge fw-semibold ${row.status === 'PAID'
            ? 'bg-success'
            : row.payment_status === 'PENDING'
              ? 'bg-warning text-dark'
              : 'bg-danger'
            }`}
        >
          {row.status}
        </span>
      ),
      center: true,
      width: '155px',
      minWidth: '155px',
    },
    {
      name: 'Action',
      cell: (row: any) => {
        const isThisRowLoading = downloadingRowId === row.id;
        const isOverageInvoice = isOverageTransaction(row);
        const disableAllButtons = isDownloadInProgress || cancelingRowId === row.id;
        const showCancelAction = canModifyCustomers && isOverageInvoice && !['CANCELLED', 'PAID'].includes(
          String(row?.status ?? '').toUpperCase()
        );

        return (
          <div className='d-flex justify-content-center align-items-center gap-2'>
            <OverlayTrigger
              placement='top'
              delay={{ show: 250, hide: 100 }}
              overlay={<Tooltip id={`download-invoice-${row.id}`}>Download Invoice</Tooltip>}
            >
              {disableAllButtons ? (
                <span className='d-inline-flex'>
                  <Button
                    variant='light'
                    size='sm'
                    className='btn-icon btn-sm btn-light-primary'
                    onClick={() => handleDownload(row)}
                    disabled={disableAllButtons}
                    aria-label='Download Invoice'
                  >
                    {isThisRowLoading ? <Spinner size='sm' animation='border' /> : <FontAwesomeIcon icon={faDownload} />}
                  </Button>
                </span>
              ) : (
                <Button
                  variant='light'
                  size='sm'
                  className='btn-icon btn-sm btn-light-primary'
                  onClick={() => handleDownload(row)}
                  disabled={disableAllButtons}
                  aria-label='Download Invoice'
                >
                  {isThisRowLoading ? <Spinner size='sm' animation='border' /> : <FontAwesomeIcon icon={faDownload} />}
                </Button>
              )}
            </OverlayTrigger>

            {showCancelAction && (
              <OverlayTrigger
                placement='top'
                delay={{ show: 250, hide: 100 }}
                overlay={<Tooltip id={`cancel-invoice-${row.id}`}>Cancel Invoice</Tooltip>}
              >
                {disableAllButtons ? (
                  <span className='d-inline-flex'>
                    <Button
                      variant='light'
                      size='sm'
                      className='btn-icon btn-sm btn-light-danger'
                      onClick={() => {
                        setCancelTargetRow(row);
                        setShowCancelConfirm(true);
                      }}
                      disabled={disableAllButtons}
                      aria-label='Cancel Invoice'
                    >
                      {cancelingRowId === row.id ? <Spinner size='sm' animation='border' /> : <FontAwesomeIcon icon={faBan} />}
                    </Button>
                  </span>
                ) : (
                  <Button
                    variant='light'
                    size='sm'
                    className='btn-icon btn-sm btn-light-danger'
                    onClick={() => {
                      setCancelTargetRow(row);
                      setShowCancelConfirm(true);
                    }}
                    disabled={disableAllButtons}
                    aria-label='Cancel Invoice'
                  >
                    {cancelingRowId === row.id ? <Spinner size='sm' animation='border' /> : <FontAwesomeIcon icon={faBan} />}
                  </Button>
                )}
              </OverlayTrigger>
            )}
          </div>
        );
      },
      center: true,
      width: '108px',
      minWidth: '100px',
    },
    {
      name: '',
      id: 'overage-usage-summary',
      cell: (row: any) => {
        if (!expandedRowIds[row.id] || !hasExpandableOverageDetails(row)) {
          return null
        }
        return <OverageUsageSummaryPanel row={row} />
      },
      conditionalCellStyles: [
        {
          when: (row: any) => !expandedRowIds[row.id] || !hasExpandableOverageDetails(row),
          style: {
            display: 'none',
            padding: 0,
            flex: '0 0 0',
            width: 0,
            minWidth: 0,
            maxWidth: 0,
            border: 'none',
          },
        },
        {
          when: (row: any) => !!expandedRowIds[row.id] && hasExpandableOverageDetails(row),
          style: {
            display: 'flex',
            flex: '0 0 100%',
            width: '100%',
            minWidth: '100%',
            maxWidth: 'none',
            alignSelf: 'stretch',
          },
        },
      ],
      sortable: false,
      width: '0px',
      minWidth: '0',
      allowOverflow: true,
      center: false,
    },
  ],
    [
      expandedRowIds,
      downloadingRowId,
      isDownloadInProgress,
      cancelingRowId,
      canModifyCustomers,
      filters,
      openFilterKey,
    ]
  )

  return (
    <div style={{ paddingLeft: '10px', paddingRight: '10px', paddingBottom: "10px" }}>

      <div className='mb-3 d-flex flex-wrap align-items-center justify-content-between gap-3'>
        <span className='fw-bold fs-4 text-dark'>Invoice Details</span>
        <div className='d-flex flex-wrap align-items-center gap-2'>
          <div className='invoice-details-search-wrap'>
            <i className='bi bi-search' aria-hidden='true' />
            <input
              className='form-control invoice-details-search-input'
              type='search'
              placeholder='Search by invoice number'
              aria-label='Search transactions by invoice number'
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
          <button
            type='button'
            className='btn btn-sm btn-primary fw-bold px-4'
            onClick={handleResetFilters}
            disabled={!hasActiveInvoiceFilters}
          >
            Reset Filters
          </button>
        </div>
      </div>

      <div className='card card-flush' style={{ borderRadius: '3px' }}>
        <div className='card-body' style={{ padding: 0 }}>
          <TableLoadingShell loading={loading}>
          <div className='invoice-details-datatable'>
          <DataTable
            fixedHeader
            persistTableHead
            columns={columns}
            data={tableRows}
            conditionalRowStyles={[
              {
                when: (row: any) => !!expandedRowIds[row.id] && hasExpandableOverageDetails(row),
                classNames: ['invoice-row-overage-expanded'],
              },
            ]}
            progressPending={false}
            noDataComponent={
              <div className='text-secondary'>
                <h5>No invoices found</h5>
              </div>
            }
            pagination
            paginationServer
            paginationTotalRows={pagination.count}
            paginationPerPage={paginationData.page_limit}
            sortServer
            onSort={handleSort}
            onChangePage={(page) =>
              setPaginationData((prev) => ({ ...prev, page_num: page }))
            }
            onChangeRowsPerPage={(newLimit) =>
              setPaginationData((prev) => ({
                ...prev,
                page_limit: newLimit,
                page_num: 1,
              }))
            }
            customStyles={{
              table: {
                style: {
                  height: '55vh',
                },
              },
              headCells: {
                style: {
                  fontWeight: 'bold',
                  fontSize: '13px',
                  paddingLeft: '6px',
                  paddingRight: '6px',
                  backgroundColor: 'rgba(33,37,41,0.03)', // header background inside table
                  textAlign: 'center', // optional: center text
                },
              },
              cells: {
                style: {
                  padding: '8px 6px',
                  borderBottom: '1px solid gainsboro',
                  textAlign: 'center', // center all table cells
                },
              },
            }}
          />
          </div>
          </TableLoadingShell>
        </div>
      </div>

      <CommonConfirmationModal
        show={showCancelConfirm}
        title='Cancel Overage Invoice'
        message='Are you sure you want to cancel this overage invoice? This will cancel it on PayPal and update the Commercium transaction status.'
        confirmText='Yes, Cancel Invoice'
        confirmVariant='danger'
        onConfirm={handleCancelInvoice}
        onCancel={() => {
          setShowCancelConfirm(false)
          setCancelTargetRow(null)
        }}
      />
    </div>
  )
}

export default InvoiceDetailsGeneration
